import React from 'react';
import styles from './ChatBubble.module.css';
import { AIMessage } from './AIMessage';
import { MessageFeedback } from '../../../services/SessionService';

// A backtest run stamps the submitted config onto its trigger message, wrapped in
// these markers. The chat renders that as a structured config card (below), not raw text.
const SUBMIT_SUMMARY_START = '<!-- BACKTEST_SUBMIT_SUMMARY_START -->';
const SUBMIT_SUMMARY_END = '<!-- BACKTEST_SUBMIT_SUMMARY_END -->';

const extractBacktestSubmitSummary = (text: string): string | null => {
  const start = text.indexOf(SUBMIT_SUMMARY_START);
  const end = text.indexOf(SUBMIT_SUMMARY_END);
  if (start === -1 || end === -1 || end <= start) return null;
  return text.slice(start + SUBMIT_SUMMARY_START.length, end).trim();
};

// ChartData interface removed - using embedded charts only

interface ChatBubbleProps {
  messageId: string;
  user: string;
  text: string;
  feedback?: MessageFeedback | null;
  content?: {
    text?: string;
    chart?: any;
    data?: any;
    strategy_config?: any;
    embedded_chart?: any;
    backtest_progress?: {
      progress: number;
      phase: 'planning' | 'loading_data' | 'executing' | 'loading_positions' | 'complete' | 'failed';
      currentPeriod?: string;
      isComplete?: boolean;
      error?: string;
    };
  };
  onDownloadExcel?: () => void;
  sessionId?: string; // Added for chart retrieval
  onFeedbackChange?: (messageId: string, feedback: MessageFeedback | null) => Promise<boolean>;
  // chartData removed - using embedded charts only
}

export const ChatBubble: React.FC<ChatBubbleProps> = ({ 
  messageId,
  user, 
  text, 
  feedback,
  content,
  onDownloadExcel,
  sessionId,
  onFeedbackChange
}) => {

  const isUser = user === 'user';

  // For AI messages, use the new ChatGPT-style AIMessage component
  if (!isUser) {
    return (
      <AIMessage 
        messageId={messageId}
        text={text}
        feedback={feedback}
        messageContent={content}
        onDownloadExcel={onDownloadExcel}
        sessionId={sessionId}
        onFeedbackChange={onFeedbackChange}
      />
    );
  }

  // For user messages, keep the bubble style with proper formatting. When the message
  // carries an appended backtest config summary, render only the structured config card.
  const submitSummary = typeof text === 'string' ? extractBacktestSubmitSummary(text) : null;

  return (
    <div className={`${styles.container} ${styles.userContainer}`}>
      <div className={`${styles.bubble} ${styles.userBubble}`}>
        {submitSummary !== null ? (
          <div className={styles.backtestSummaryCard}>
            <span className={styles.backtestSummaryHeader}>Backtest config</span>
            <div className={styles.backtestSummaryBody}>{submitSummary}</div>
          </div>
        ) : (
          <div style={{ whiteSpace: 'pre-wrap', lineHeight: '1.6' }}>
            {text}
          </div>
        )}
      </div>
    </div>
  );
};
